import { useState, useEffect } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import Badge from '../../components/admin/Badge';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

function AdminAffiliates({ apiFetch, token, toast }) {
  const [affTab, setAffTab] = useState('settings');
  const [affLoading, setAffLoading] = useState(true);
  const [affSettings, setAffSettings] = useState({ defaultCommissionRate: '', minPayoutThreshold: '' });
  const [affAffiliates, setAffAffiliates] = useState([]);
  const [affReferrals, setAffReferrals] = useState([]);
  const [affPayouts, setAffPayouts] = useState([]);
  const [affCommissions, setAffCommissions] = useState([]);
  const [affRefFilter, setAffRefFilter] = useState('all');
  const [affPayFilter, setAffPayFilter] = useState('all');
  const [affCommFilter, setAffCommFilter] = useState('all');
  const [affNoteInputs, setAffNoteInputs] = useState({});
  const [stopConfirm, setStopConfirm] = useState(null);

  const loadAll = () => {
    setAffLoading(true);
    Promise.all([
      apiFetch('/api/admin/affiliate/settings').then(r => r.json()),
      apiFetch('/api/admin/affiliate/affiliates').then(r => r.json()),
      apiFetch('/api/admin/affiliate/referrals').then(r => r.json()),
      apiFetch('/api/admin/affiliate/payouts').then(r => r.json()),
      apiFetch('/api/admin/affiliate/commissions').then(r => r.json()),
    ]).then(([settings, affiliates, referrals, payouts, commissions]) => {
      setAffSettings({ defaultCommissionRate: settings.defaultCommissionRate ?? '', minPayoutThreshold: settings.minPayoutThreshold ?? '' });
      setAffAffiliates(Array.isArray(affiliates) ? affiliates : []);
      setAffReferrals(Array.isArray(referrals) ? referrals : []);
      setAffPayouts(Array.isArray(payouts) ? payouts : []);
      setAffCommissions(Array.isArray(commissions) ? commissions : []);
    }).catch(() => toast.error('Error', 'Failed to load affiliate data')).finally(() => setAffLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const affiliateColumns = [
    { key: 'email', header: 'Email' },
    { key: 'affiliate_code', header: 'Code', render: row => <code style={{ color: 'var(--admin-accent)' }}>{row.affiliate_code}</code> },
    { key: 'pending_balance', header: 'Pending', render: row => <span style={{ color: '#fcd34d' }}>${Number(row.pending_balance || 0).toFixed(2)}</span> },
    { key: 'approved_balance', header: 'Approved', render: row => <span style={{ color: 'var(--admin-success)' }}>${Number(row.approved_balance || 0).toFixed(2)}</span> },
    { key: 'total_paid', header: 'Total Paid', render: row => `$${Number(row.total_paid || 0).toFixed(2)}` },
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'active' ? 'active' : 'suspended'}>{row.status}</Badge> },
    { key: 'payout_details', header: 'Payout Details', cellStyle: { maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--admin-text-muted)' }, render: row => row.payout_details || '—' },
    { key: 'actions', header: 'Action', render: row => (
      <button className={`admin-btn admin-btn--sm admin-btn--${row.status === 'active' ? 'danger' : 'primary'}`} onClick={() => {
        const newStatus = row.status === 'active' ? 'suspended' : 'active';
        apiFetch(`/api/admin/affiliate/affiliates/${row.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) })
          .then(() => {
            setAffAffiliates(prev => prev.map(x => x.id === row.id ? { ...x, status: newStatus } : x));
            toast.success('Updated', newStatus === 'suspended' ? 'Affiliate suspended. All active commissions stopped.' : 'Affiliate activated.');
          }).catch(() => toast.error('Error', 'Action failed'));
      }}>{row.status === 'active' ? 'Suspend' : 'Activate'}</button>
    )},
  ];

  const referralColumns = [
    { key: 'affiliate_email', header: 'Affiliate' },
    { key: 'referred_email', header: 'Referred User', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'plan', header: 'Plan', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.plan_name || row.plan_slug || '—' },
    { key: 'rate', header: 'Rate', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => (row.commission_rate_locked || row.commission_rate) ? `${row.commission_rate_locked || row.commission_rate}%` : '—' },
    { key: 'recurring', header: 'Recurring', render: row => row.commission_active === 1 ? <Badge variant="active">Active</Badge> : row.stop_reason ? <Badge variant="suspended">{row.stop_reason.replace(/_/g, ' ')}</Badge> : <span style={{ color: 'var(--admin-text-muted)' }}>—</span> },
    { key: 'created_at', header: 'Date', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => row.created_at ? new Date(row.created_at * 1000).toLocaleDateString() : '—' },
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'approved' || row.status === 'paid' ? 'active' : row.status === 'pending' ? 'pending' : row.status === 'signup' ? 'info' : 'suspended'}>{row.status}</Badge> },
    { key: 'actions', header: 'Actions', render: row => (
      <div style={{ whiteSpace: 'nowrap', display: 'flex', gap: '4px' }}>
        {row.status === 'pending' && (
          <>
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => {
              apiFetch(`/api/admin/affiliate/referrals/${row.id}/approve`, { method: 'POST' }).then(() => {
                setAffReferrals(prev => prev.map(x => x.id === row.id ? { ...x, status: 'approved' } : x));
                toast.success('Approved', 'Referral approved. Pending commissions auto-approved.');
              });
            }}>Approve</button>
            <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => {
              apiFetch(`/api/admin/affiliate/referrals/${row.id}/cancel`, { method: 'POST' }).then(() => {
                setAffReferrals(prev => prev.map(x => x.id === row.id ? { ...x, status: 'cancelled', commission_active: 0, stop_reason: 'referral_cancelled' } : x));
                toast.success('Cancelled', 'Referral cancelled');
              });
            }}>Cancel</button>
          </>
        )}
        {row.commission_active === 1 && (
          <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setStopConfirm(row.id)}>Stop</button>
        )}
      </div>
    )},
  ];

  const commissionColumns = [
    { key: 'created_at', header: 'Date', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => fmtDate(row.created_at) },
    { key: 'affiliate_email', header: 'Affiliate' },
    { key: 'referred_email', header: 'Referred User', cellStyle: { color: 'var(--admin-text-secondary)' } },
    { key: 'plan', header: 'Plan', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.plan_name || row.plan_slug || '—' },
    { key: 'amount', header: 'Invoice Amt', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => `$${Number(row.amount || 0).toFixed(2)}` },
    { key: 'commission_amount', header: 'Commission', render: row => <span style={{ color: 'var(--admin-success)', fontWeight: '700' }}>${Number(row.commission_amount || 0).toFixed(2)}</span> },
    { key: 'commission_rate', header: 'Rate', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => `${row.commission_rate}%` },
    { key: 'period', header: 'Period', cellStyle: { color: 'var(--admin-text-muted)', fontSize: '11px' }, render: row => row.billing_period_start ? `${fmtDate(row.billing_period_start)} - ${fmtDate(row.billing_period_end)}` : '—' },
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'approved' || row.status === 'paid' ? 'active' : row.status === 'pending' ? 'pending' : 'suspended'}>{row.status}</Badge> },
    { key: 'actions', header: 'Actions', render: row => row.status === 'pending' ? (
      <div style={{ display: 'flex', gap: '4px' }}>
        <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => {
          apiFetch(`/api/admin/affiliate/commissions/${row.id}/approve`, { method: 'POST' }).then(() => {
            setAffCommissions(prev => prev.map(x => x.id === row.id ? { ...x, status: 'approved' } : x));
            toast.success('Approved', 'Commission approved');
          });
        }}>Approve</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => {
          apiFetch(`/api/admin/affiliate/commissions/${row.id}/reject`, { method: 'POST' }).then(() => {
            setAffCommissions(prev => prev.map(x => x.id === row.id ? { ...x, status: 'rejected' } : x));
          });
        }}>Reject</button>
      </div>
    ) : null },
  ];

  const payoutColumns = [
    { key: 'affiliate_email', header: 'Affiliate Email' },
    { key: 'amount', header: 'Amount', render: row => <span style={{ color: 'var(--admin-success)', fontWeight: '700' }}>${Number(row.amount || 0).toFixed(2)}</span> },
    { key: 'payout_details', header: 'Payout Details', cellStyle: { maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--admin-text-muted)' }, render: row => row.payout_details || '—' },
    { key: 'requested_at', header: 'Requested', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => row.requested_at ? new Date(row.requested_at * 1000).toLocaleDateString() : '—' },
    { key: 'status', header: 'Status', render: row => <Badge variant={row.status === 'paid' ? 'active' : row.status === 'requested' ? 'info' : 'rejected'}>{row.status}</Badge> },
    { key: 'note', header: 'Note', cellStyle: { color: 'var(--admin-text-muted)' }, render: row => row.note || '—' },
    { key: 'actions', header: 'Actions', render: row => row.status === 'requested' ? (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="admin-input" type="text" placeholder="Note" value={affNoteInputs[row.id] || ''} onChange={e => setAffNoteInputs(prev => ({ ...prev, [row.id]: e.target.value }))} style={{ width: '100px', fontSize: '11px' }} />
        <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => {
          apiFetch(`/api/admin/affiliate/payouts/${row.id}/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: affNoteInputs[row.id] || '' }) })
            .then(() => { setAffPayouts(prev => prev.map(x => x.id === row.id ? { ...x, status: 'paid', note: affNoteInputs[row.id] || '' } : x)); toast.success('Completed', 'Payout marked as paid'); });
        }}>Complete</button>
        <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => {
          const note = affNoteInputs[row.id] || '';
          if (!note) { toast.warning('Required', 'Enter a rejection note'); return; }
          apiFetch(`/api/admin/affiliate/payouts/${row.id}/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) })
            .then(() => { setAffPayouts(prev => prev.map(x => x.id === row.id ? { ...x, status: 'rejected', note } : x)); toast.success('Rejected', 'Payout rejected'); });
        }}>Reject</button>
      </div>
    ) : null },
  ];

  return (
    <div>
      <SectionHeader title="Affiliates" />

      <div className="admin-tabs" style={{ marginBottom: '16px' }}>
        {['settings', 'affiliates', 'referrals', 'commissions', 'payouts'].map(t => (
          <button key={t} className={`admin-tab ${affTab === t ? 'admin-tab--active' : ''}`} onClick={() => setAffTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {/* Settings */}
      {affTab === 'settings' && (
        <div className="admin-card" style={{ maxWidth: '420px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label className="admin-label">DEFAULT COMMISSION RATE (%)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input className="admin-input" type="number" min="0" max="100" step="0.1" value={affSettings.defaultCommissionRate} onChange={e => setAffSettings(prev => ({ ...prev, defaultCommissionRate: e.target.value }))} placeholder="e.g. 20" style={{ width: '120px' }} />
            </div>
            <p style={{ margin: '6px 0 0 0', color: 'var(--admin-text-muted)', fontSize: '11px' }}>Plans with a custom commission rate override this default.</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label className="admin-label">MINIMUM PAYOUT THRESHOLD ($)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input className="admin-input" type="number" min="0" step="0.01" value={affSettings.minPayoutThreshold} onChange={e => setAffSettings(prev => ({ ...prev, minPayoutThreshold: e.target.value }))} placeholder="e.g. 50" style={{ width: '120px' }} />
            </div>
            <p style={{ margin: '6px 0 0 0', color: 'var(--admin-text-muted)', fontSize: '11px' }}>Affiliates must reach this balance before they can request a payout. Set to 0 to disable.</p>
          </div>
          <button className="admin-btn admin-btn--primary" onClick={() => {
            const rate = parseFloat(affSettings.defaultCommissionRate);
            const threshold = parseFloat(affSettings.minPayoutThreshold || '0');
            if (isNaN(rate)) { toast.error('Error', 'Enter a valid rate'); return; }
            if (isNaN(threshold) || threshold < 0) { toast.error('Error', 'Enter a valid threshold'); return; }
            apiFetch('/api/admin/affiliate/settings', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ defaultCommissionRate: rate, minPayoutThreshold: threshold }) })
              .then(r => r.json()).then(d => toast.success('Saved', d.ok ? 'Settings saved' : d.error)).catch(() => toast.error('Error', 'Request failed'));
          }}>Save Settings</button>
        </div>
      )}

      {/* Affiliates */}
      {affTab === 'affiliates' && (
        <div className="admin-card">
          <DataTable columns={affiliateColumns} data={affAffiliates} loading={affLoading} emptyMessage="No affiliates yet" emptyIcon="fa-solid fa-users" />
        </div>
      )}

      {/* Referrals */}
      {affTab === 'referrals' && (
        <div className="admin-card">
          <div className="admin-filter-pills" style={{ marginBottom: '12px' }}>
            {['all', 'signup', 'pending', 'approved', 'paid', 'cancelled'].map(s => (
              <button key={s} className={`admin-pill ${affRefFilter === s ? 'admin-pill--active' : ''}`} onClick={() => {
                setAffRefFilter(s);
                apiFetch(`/api/admin/affiliate/referrals${s !== 'all' ? `?status=${s}` : ''}`).then(r => r.json()).then(d => setAffReferrals(Array.isArray(d) ? d : []));
              }}>{s}</button>
            ))}
          </div>
          <DataTable columns={referralColumns} data={affReferrals} loading={affLoading} emptyMessage="No referrals found" emptyIcon="fa-solid fa-link" />
        </div>
      )}

      {/* Commissions */}
      {affTab === 'commissions' && (
        <div className="admin-card">
          <div className="admin-filter-pills" style={{ marginBottom: '12px' }}>
            {['all', 'pending', 'approved', 'paid', 'rejected'].map(s => (
              <button key={s} className={`admin-pill ${affCommFilter === s ? 'admin-pill--active' : ''}`} onClick={() => {
                setAffCommFilter(s);
                apiFetch(`/api/admin/affiliate/commissions${s !== 'all' ? `?status=${s}` : ''}`).then(r => r.json()).then(d => setAffCommissions(Array.isArray(d) ? d : []));
              }}>{s}</button>
            ))}
          </div>
          <DataTable columns={commissionColumns} data={affCommissions} loading={affLoading} emptyMessage="No commissions found" emptyIcon="fa-solid fa-coins" />
        </div>
      )}

      {/* Payouts */}
      {affTab === 'payouts' && (
        <div className="admin-card">
          <div className="admin-filter-pills" style={{ marginBottom: '12px' }}>
            {['all', 'requested', 'paid', 'rejected'].map(s => (
              <button key={s} className={`admin-pill ${affPayFilter === s ? 'admin-pill--active' : ''}`} onClick={() => {
                setAffPayFilter(s);
                apiFetch(`/api/admin/affiliate/payouts${s !== 'all' ? `?status=${s}` : ''}`).then(r => r.json()).then(d => setAffPayouts(Array.isArray(d) ? d : []));
              }}>{s}</button>
            ))}
          </div>
          <DataTable columns={payoutColumns} data={affPayouts} loading={affLoading} emptyMessage="No payouts found" emptyIcon="fa-solid fa-money-bill-wave" />
        </div>
      )}

      {/* Stop commissions confirm */}
      <ConfirmDialog
        open={!!stopConfirm}
        onClose={() => setStopConfirm(null)}
        onConfirm={() => {
          apiFetch(`/api/admin/affiliate/referrals/${stopConfirm}/stop-commissions`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: 'admin_stopped' })
          }).then(() => {
            setAffReferrals(prev => prev.map(x => x.id === stopConfirm ? { ...x, commission_active: 0, stop_reason: 'admin_stopped' } : x));
            toast.success('Stopped', 'Recurring commissions stopped');
            setStopConfirm(null);
          });
        }}
        title="Stop Recurring Commissions"
        message="Are you sure you want to stop all recurring commissions for this referral?"
        confirmText="Stop Commissions"
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminAffiliates;
